import * as React from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Download, FileSpreadsheet, FileText, Printer, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

export type ExportFormat = 'csv' | 'xlsx' | 'pdf' | 'print';

export interface ExportOption {
  format: ExportFormat;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

export interface ExportMenuProps {
  onExport: (format: ExportFormat) => void;
  label?: string;
  variant?: 'default' | 'secondary' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  loadingFormat?: ExportFormat | null;
  disabledFormats?: ExportFormat[];
  options?: Partial<Record<ExportFormat, Omit<ExportOption, 'format'>>>;
}

const defaultExportOptions: Record<ExportFormat, Omit<ExportOption, 'format'>> = {
  csv: {
    label: 'CSV',
    icon: <FileSpreadsheet className="h-4 w-4" />,
  },
  xlsx: {
    label: 'Excel (.xlsx)',
    icon: <FileSpreadsheet className="h-4 w-4" />,
  },
  pdf: {
    label: 'PDF',
    icon: <FileText className="h-4 w-4" />,
  },
  print: {
    label: 'Print',
    icon: <Printer className="h-4 w-4" />,
  },
};

export const ExportMenu = React.forwardRef<HTMLButtonElement, ExportMenuProps>(
  (
    {
      onExport,
      label = 'Export',
      variant = 'outline',
      size = 'default',
      className,
      loadingFormat,
      disabledFormats = [],
      options = {},
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);

    const mergedOptions: Record<ExportFormat, ExportOption> = {
      csv: {
        format: 'csv',
        ...defaultExportOptions.csv,
        ...options.csv,
      },
      xlsx: {
        format: 'xlsx',
        ...defaultExportOptions.xlsx,
        ...options.xlsx,
      },
      pdf: {
        format: 'pdf',
        ...defaultExportOptions.pdf,
        ...options.pdf,
      },
      print: {
        format: 'print',
        ...defaultExportOptions.print,
        ...options.print,
      },
    };

    const formatList: ExportFormat[] = ['csv', 'xlsx', 'pdf', 'print'];

    const handleExport = (format: ExportFormat) => {
      onExport(format);
      setOpen(false);
    };

    const isFormatDisabled = (format: ExportFormat): boolean => {
      return disabledFormats.includes(format) || mergedOptions[format]?.disabled === true;
    };

    const isFormatLoading = (format: ExportFormat): boolean => {
      return loadingFormat === format;
    };

    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            ref={ref}
            variant={variant}
            size={size}
            className={cn('gap-2', className)}
            disabled={loadingFormat !== null && loadingFormat !== undefined}
          >
            {loadingFormat ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {size !== 'icon' && label}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs font-semibold text-[var(--color-muted-foreground)]">
            Export as
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {formatList.map((format) => {
            const option = mergedOptions[format];
            const disabled = isFormatDisabled(format);
            const loading = isFormatLoading(format);

            return (
              <DropdownMenuItem
                key={format}
                onClick={() => !disabled && !loading && handleExport(format)}
                disabled={disabled || loading}
                className={cn('gap-2', {
                  'opacity-50 cursor-not-allowed': disabled,
                })}
              >
                <div className="flex items-center gap-2 w-full">
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <div className="text-[var(--color-muted-foreground)]">
                      {option.icon}
                    </div>
                  )}
                  <span className="flex-1">{option.label}</span>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);

ExportMenu.displayName = 'ExportMenu';
